// What deleting one row does in the database: which tables lose rows along with it (cascade), whose
// foreign keys become null (set null), and which stop the delete when they hold rows (restrict or no
// action). It follows the referential actions migrations create for each relationship's onDelete, so
// EF Core's client-only behaviors (ClientSetNull, ClientCascade) count as NO ACTION, which they are
// in the database. SQL Server allows one path of cascading actions to each table and no cycles;
// PostgreSQL allows any number. Only the logical model is read, except to guess the database.
import { rootOfPath, specAt } from '../model/logical';
import type { ComplexSpec, DeleteBehavior, EntitySpec, ModelSpec, OwnedSpec, PhysicalSpec } from '../model/schema';

export type DeleteDatabase = 'sqlServer' | 'postgres';

/** What happens to a dependent's rows when the row they point at is deleted. */
export type DeleteEffect = 'delete' | 'setNull' | 'block';

/** The referential action of a foreign key, as the database has it. */
export type ReferentialAction = 'CASCADE' | 'SET NULL' | 'RESTRICT' | 'NO ACTION';

export interface DeleteStep {
  /** Index into model.relationships. */
  relationship: number;
  /** The relationship's principal (entity key or owned path). */
  principal: string;
  /** The entity the principal belongs to. */
  principalEntity: string;
  /** The relationship's dependent (entity key or owned path): where the foreign key is. */
  dependent: string;
  /** The entity the dependent belongs to. */
  entity: string;
  /** Effect `delete`: whose rows go (the dependent, or the owner of an owned type kept in its table). */
  deletes?: string;
  foreignKey: string[];
  effect: DeleteEffect;
  action: ReferentialAction;
  /** What EF Core does beyond the database for dependents it has loaded (ClientCascade, ClientSetNull). */
  client?: 'delete' | 'setNull';
  /** Relationship indexes from the start to here, this step last: the shortest way it is reached. */
  path: number[];
  /** Applies only when the deleted row is of this derived type. */
  onlyIf?: string;
  /** PostgreSQL NO ACTION: the dependent's rows are deleted along another path as well, and only rows left over block. */
  alsoDeleted?: boolean;
}

/** SQL Server: a table that cascading actions reach along more than one path, or around a cycle. It refuses the foreign keys. */
export interface DeleteConflict {
  /** The table's entity (or owned path). */
  entity: string;
  /** Indexes into `steps`: the cascading steps that reach it. */
  steps: number[];
  /** One of the paths comes back to a table it already passed (the start included). */
  cycle: boolean;
}

export interface DeleteImpact {
  start: string;
  database: DeleteDatabase;
  /** Every relationship reached from a deleted row, in order of steps from the start. */
  steps: DeleteStep[];
  /** Entities and owned paths whose rows are deleted along with the start's (the start excluded), in the order reached. */
  deleted: string[];
  /** SQL Server only. */
  conflicts: DeleteConflict[];
}

/** The referential action a migration creates for a delete behavior. SQL Server has no RESTRICT; EF Core creates NO ACTION. */
export function referentialAction(behavior: DeleteBehavior, database: DeleteDatabase): ReferentialAction {
  switch (behavior) {
    case 'cascade':
      return 'CASCADE';
    case 'setNull':
      return 'SET NULL';
    case 'restrict':
      return database === 'postgres' ? 'RESTRICT' : 'NO ACTION';
    default:
      return 'NO ACTION';
  }
}

const EFFECT: Record<ReferentialAction, DeleteEffect> = { CASCADE: 'delete', 'SET NULL': 'setNull', RESTRICT: 'block', 'NO ACTION': 'block' };

/**
 * The database a model was made for, from what its provider left in it (annotations and calls such as
 * `SqlServer:ValueGenerationStrategy` or `UseIdentityByDefaultColumns`, and the identifier length:
 * 128 on SQL Server, 63 on PostgreSQL). Null when it does not say, or is another database.
 */
export function guessDatabase(model: ModelSpec): DeleteDatabase | null {
  let sqlServer = 0;
  let postgres = 0;
  const read = (p: { annotations?: PhysicalSpec['annotations']; calls?: string[] } | undefined) => {
    for (const [key, value] of Object.entries(p?.annotations ?? {})) {
      if (key.startsWith('SqlServer:')) sqlServer++;
      else if (key.startsWith('Npgsql:')) postgres++;
      else if (key === 'Relational:MaxIdentifierLength') {
        if (value === 128) sqlServer++;
        else if (value === 63) postgres++;
      }
    }
    for (const call of p?.calls ?? []) {
      if (call.includes('SqlServer')) sqlServer++;
      else if (call.includes('Npgsql')) postgres++;
    }
  };
  const walk = (c: EntitySpec | OwnedSpec | ComplexSpec) => {
    read(c.physical);
    for (const p of Object.values(c.properties)) read(p.physical);
    for (const x of Object.values(c.complex ?? {})) walk(x);
    if ('owned' in c) for (const o of Object.values(c.owned ?? {})) walk(o);
  };
  read(model.physical);
  for (const e of Object.values(model.entities)) walk(e);
  if (sqlServer === postgres) return null;
  return sqlServer > postgres ? 'sqlServer' : 'postgres';
}

/** Base types of an entity (nearest first). */
function basesOf(model: ModelSpec, key: string): string[] {
  const out: string[] = [];
  for (let b = model.entities[key]?.base; b && !out.includes(b) && b !== key; b = model.entities[b]?.base) out.push(b);
  return out;
}

/**
 * Where a dependent's rows are: an owned type kept in its owner's table (one, not JSON, no table of
 * its own) is the owner's row, so a cascade on its foreign key deletes the owner's row.
 */
function rowHolder(model: ModelSpec, name: string): string {
  if (model.entities[name]) return name;
  const spec = specAt(model, name) as OwnedSpec | undefined;
  if (!spec || spec.collection || spec.mapping?.table) return name;
  return rowHolder(model, name.slice(0, name.lastIndexOf('.')));
}

/** A name for the table holding a row of `name`, the same for every entity sharing it. */
export function tableOf(model: ModelSpec, name: string): string {
  const holder = rowHolder(model, name);
  const e = model.entities[holder];
  if (!e) {
    const o = specAt(model, holder) as OwnedSpec | undefined;
    return o?.mapping?.table ? `${o.mapping.schema ?? model.defaultSchema ?? ''}.${o.mapping.table}` : holder;
  }
  // Derived types without a table of their own share their base's (TPH).
  for (const k of [holder, ...basesOf(model, holder)]) {
    const m = model.entities[k]?.mapping;
    if (m?.table) return `${m.schema ?? model.defaultSchema ?? ''}.${m.table}`;
    if (m?.table === null || m?.view) return k;
  }
  return [holder, ...basesOf(model, holder)].at(-1)!;
}

/** Everything that happens when one row of `start` (an entity key or owned path) is deleted. */
export function deleteImpact(model: ModelSpec, start: string, database: DeleteDatabase): DeleteImpact {
  const rels = model.relationships;
  // Derived types per entity, to find relationships of the row's possible types.
  const derived = new Map<string, string[]>();
  for (const [key, e] of Object.entries(model.entities)) if (e.base) derived.set(e.base, [...(derived.get(e.base) ?? []), key]);
  const descendants = (key: string): string[] => {
    const out: string[] = [];
    const stack = [...(derived.get(key) ?? [])];
    while (stack.length) {
      const d = stack.pop()!;
      if (out.includes(d) || d === key) continue;
      out.push(d);
      stack.push(...(derived.get(d) ?? []));
    }
    return out;
  };

  /**
   * Relationships whose principal goes when a row of `name` is deleted: its own, its owned types',
   * its base types' (the row is one of them too), and its derived types' (when the row is one).
   */
  const reachedFrom = (name: string): { index: number; onlyIf?: string }[] => {
    const out: { index: number; onlyIf?: string }[] = [];
    const isEntity = !!model.entities[name];
    const below = isEntity ? descendants(name) : [];
    const family = isEntity ? new Set([name, ...basesOf(model, name)]) : new Set<string>();
    rels.forEach((r, index) => {
      if (r.principal === name || r.principal.startsWith(`${name}.`)) return void out.push({ index });
      if (!isEntity) return;
      const root = rootOfPath(model, r.principal).root;
      if (family.has(root)) out.push({ index });
      else if (below.includes(root)) out.push({ index, onlyIf: root });
    });
    return out;
  };

  const steps: DeleteStep[] = [];
  const deleted: string[] = [];
  const isDeleted = new Set([start]);
  const done = new Set<number>();
  const queue: { name: string; path: number[] }[] = [{ name: start, path: [] }];
  while (queue.length) {
    const { name, path } = queue.shift()!;
    for (const { index, onlyIf } of reachedFrom(name)) {
      if (done.has(index)) continue;
      done.add(index);
      const r = rels[index]!;
      const action = referentialAction(r.onDelete, database);
      const step: DeleteStep = {
        relationship: index,
        principal: r.principal,
        principalEntity: rootOfPath(model, r.principal).root,
        dependent: r.dependent,
        entity: rootOfPath(model, r.dependent).root,
        foreignKey: r.foreignKey,
        effect: EFFECT[action],
        action,
        path: [...path, index],
      };
      if (r.onDelete === 'clientCascade') step.client = 'delete';
      if (r.onDelete === 'clientSetNull') step.client = 'setNull';
      if (onlyIf) step.onlyIf = onlyIf;
      steps.push(step);
      if (step.effect !== 'delete') continue;
      const holder = rowHolder(model, r.dependent);
      step.deletes = holder;
      if (isDeleted.has(holder)) continue;
      isDeleted.add(holder);
      deleted.push(holder);
      queue.push({ name: holder, path: step.path });
    }
  }

  if (database === 'postgres') {
    // NO ACTION is checked when the statement ends, after every cascade has run.
    for (const s of steps) if (s.action === 'NO ACTION' && isDeleted.has(rowHolder(model, s.dependent))) s.alsoDeleted = true;
  }

  const conflicts: DeleteConflict[] = [];
  if (database === 'sqlServer') {
    const startTable = tableOf(model, start);
    const into = new Map<string, number[]>();
    steps.forEach((s, i) => {
      if (s.effect === 'block') return;
      const table = tableOf(model, s.dependent);
      into.set(table, [...(into.get(table) ?? []), i]);
    });
    for (const [table, list] of into) {
      if (list.length + (table === startTable ? 1 : 0) < 2) continue;
      const cycle = list.some((i) => {
        const s = steps[i]!;
        const passed = [start, ...s.path.slice(0, -1).map((r) => rels[r]!.dependent)].map((n) => tableOf(model, n));
        return passed.includes(table);
      });
      conflicts.push({ entity: steps[list[0]!]!.dependent, steps: list, cycle });
    }
  }

  return { start, database, steps, deleted, conflicts };
}
